/* eslint-disable no-alert */
import { useEffect, useMemo, useState } from 'react';
import { useHistory, withRouter, useLocation } from 'react-router-dom';
import ReactGA from 'react-ga4';
import Moment from 'moment';
import './index.css';
import './App.scss';
import Routes from './Routes';
import AuthContext from './Context/AuthContext';
import ServiceStatusContext from './Context/ServiceStatusContext';
import StatusService from './Services/StatusService';
import AuthService from './Services/AuthService';
import TokenService from './Services/TokenService';
import Navigation from './Pages/WebApp/Navigation/Navigation';
import Sidebar from './Pages/WebApp/Navigation/Sidebar';
import Footer from './Pages/WebApp/Footer/Footer';
import AppBanner from './Components/AppBanner/AppBanner';
import SystemAdminService from './Services/SystermAdminService';

/* let me merge this, this should work */
ReactGA.initialize('G-LR764V0GL8');
ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

function App() {
  const history = useHistory();
  const location = useLocation();
  const [authState, setAuthState] = useState({});
  const [serviceStatusMessages, setServiceStatusMessages] = useState([]);
  const [impersonationState, setImpersonationState] = useState({});
  const [showBanner, setShowBanner] = useState(false);
  const [lastFetchedServiceStatusTime, setLastFetchedServiceStatusTime] =
    useState(null);

  // if page is refreshed we gotta call
  useEffect(() => {
    async function CheckImpersonation() {
      await SystemAdminService.isImpersonating()
        .then((response) => {
          if (!response.data) return;

          const impersonated = {
            id: response.data.customerId,
            name: response.data.customerName,
          };
          TokenService.setImpersonationToken(impersonated);
          setImpersonationState(impersonated);
          setShowBanner(true);
        })
        .catch(() => {});
    }

    const user = TokenService.getLocalUser();
    if (!user) history.push('/login');

    if (user.role !== 10) return;
    CheckImpersonation();
  }, []);

  const [count, setCount] = useState(0);
  const refreshCheck = async () => {
    // Check if it's time to refresh token
    setCount(count + 1);

    const now = Moment();
    const refreshTime = Moment(TokenService.getExp()).subtract(20, 'minutes');

    if (now.isAfter(refreshTime)) {
      await AuthService.refresh()
        .then((response) => {
          TokenService.setToken(response.data);
          const user = TokenService.getLocalUser();
          setAuthState((state) => ({ ...state, user }));
        })
        .catch(() => {
          alert('Utloggad');
          TokenService.removeToken();
          localStorage.clear();
          sessionStorage.clear();
          setAuthState((state) => ({ ...state, user: null }));
          history.push('/login');
        });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => refreshCheck(), 60000); // Check every minute
    return () => clearTimeout(timer);
  }, [count]);

  useEffect(() => {
    const getServiceStatus = async () => {
      if (
        lastFetchedServiceStatusTime !== null &&
        Moment().subtract(10, 'seconds') < lastFetchedServiceStatusTime
      )
        return;

      await StatusService.getServiceStatusMessages()
        .then((response) => {
          setServiceStatusMessages(response.data);
        })
        .catch(() => {
          setServiceStatusMessages([]);
        })
        .finally(() => {
          setLastFetchedServiceStatusTime(Moment());
        });
    };

    const user = TokenService.getLocalUser();
    if (!user) history.push('/login');

    setAuthState((state) => ({ ...state, user }));

    const now = Moment();
    const refreshTokenExp = Moment(TokenService.getExp()).add(1380, 'minute');

    if (now.isAfter(refreshTokenExp)) {
      alert('Utloggad på grund av inaktivitet');
      TokenService.removeToken();
      localStorage.clear();
      sessionStorage.clear();
      setAuthState((state) => ({ ...state, user: null }));
      history.push('/login');
    }

    const impersonated = TokenService.getImpersonationToken();
    if (!impersonated) setShowBanner(false);
    else {
      setImpersonationState(impersonated);
      setShowBanner(true);
    }

    getServiceStatus();
  }, [location, history]);

  const authContextValue = useMemo(
    () => [authState, setAuthState],
    [authState]
  );

  const serviceStatusContextValue = useMemo(
    () => [serviceStatusMessages, setServiceStatusMessages],
    [serviceStatusMessages]
  );

  const stopImpersonating = async () => {
    await SystemAdminService.stopImpersonating()
      .then(() => {
        TokenService.removeImpersonationToken();
        setImpersonationState({});
        history.push('/app/systemadmin/customers');
      })
      .catch(() => {});
  };

  return (
    <div className="app">
      {Object.keys(authState).length !== 0 ? (
        <AuthContext.Provider value={authContextValue}>
          <ServiceStatusContext.Provider value={serviceStatusContextValue}>
            {showBanner && (
              <AppBanner
                text={`Du ser nu klienter från ${
                  impersonationState?.name ?? '[Insert customer name]'
                }'s perspektiv`}
                closeText="Avsluta"
                onClose={() => stopImpersonating()}
              />
            )}
            <div
              className={`app-main ${
                showBanner ? 'app-main-banner-margin' : ''
              }`}
            >
              <Sidebar />
              <div className="app-content">
                <Navigation />
                <div className="app-scrollable-content">
                  <Routes />
                </div>
                <Footer />
              </div>
            </div>
          </ServiceStatusContext.Provider>
        </AuthContext.Provider>
      ) : (
        ''
      )}
    </div>
  );
}

export default withRouter(App);
